import { CommonModule } from '@angular/common';
import {
  Component,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiTreeSelectComponent,
  QaiLinkButtonComponent,
  QaiSelectedSimulationsListComponent,
  SimulationItem,
  DraggableItem,
  SelectedOptions,
} from '@qai/ui';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { forkJoin } from 'rxjs';
import { ISimulationByModuleAssignment } from 'src/app/interfaces/self-serve/assignment-manager/responses/isimulation-by-module-assignment';
import { AssignmentConstants } from 'src/app/utils/constants/self-serve/assignment.constants';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { IModuleWithCalls } from 'src/app/interfaces/self-serve/responses/imodule-with-calls';
import {
  IParsedModuleWithSimulations,
  IParsedSimulation,
} from 'src/app/interfaces/self-serve/ui/iparsed-module-tree';

const attemptsOptions = [
  { value: 'unlimited', label: 'Unlimited' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' },
];

@Component({
  selector: 'add-simulation-to-assignment-modal',
  standalone: true,
  templateUrl: './add-simulation-to-assignment.component.html',
  styleUrls: ['./add-simulation-to-assignment.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiTreeSelectComponent,
    QaiLinkButtonComponent,
    QaiSelectedSimulationsListComponent,
    QaiFieldComponent,
  ],
})
export class AddSimulationModalComponent {
  @ViewChild('dialog') qaiDialogRef: QaiDialogComponent | undefined;
  @ViewChild(QaiTreeSelectComponent) treeSelectComponent!: QaiTreeSelectComponent;

  @Input() open: boolean = false;
  @Input() moduleAssignmentUuid: string = '';
  @Input() moduleUuid: string = '';
  @Input() modalHeading: string = 'Add simulation to assignment';
  @Input() isCreateAssignmentFlow: boolean = false;
  @Output() simulationAdded = new EventEmitter<string>();
  @Output() simulationAssigned = new EventEmitter<void>();
  @Output() backAndCloseModal = new EventEmitter<void>();
  @Output() closeSimulationModal = new EventEmitter<void>();

  treeData: IParsedModuleWithSimulations[] = [];
  allModulesWithCalls: IModuleWithCalls[] = [];
  selectedSimulationIds: string[] = [];
  selectedSimulations: SimulationItem[] = [];
  fieldLabel: string = 'Search for simulation';

  networkRequestInProgress: boolean = false;
  assignedSimulationIds: string[] = [];
  maxCallOrder: number = 0;
  showAttemptsAllowed: boolean = true;

  constructor(
    private authorService: AuthorService,
    private assignmentsService: AssignmentsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        this.selectedSimulationIds = [];
        this.selectedSimulations = [];
        this.treeData = [];
        this.fetchSimulationsData();
        this.openModal();
      } else {
        if (this.qaiDialogRef) {
          this.qaiDialogRef.close();
        }
      }
    }
  }

  handleSuccessClick() {
    this.simulationAdded.emit();
  }

  handleBackAndCloseClick() {
    this.backAndCloseModal.emit();
  }

  handleCloseModal() {
    this.treeData = [];
    this.closeSimulationModal.emit();
  }

  openModal() {
    this.selectedSimulations = [];
    this.selectedSimulationIds = [];
    this.updateFieldLabel();

    if (this.treeSelectComponent) {
      this.treeSelectComponent.deselectAll();
      this.treeSelectComponent.searchTerm = '';
      this.treeSelectComponent.filterTreeData();
    }

    if (this.qaiDialogRef) {
      this.qaiDialogRef.showModal();
    }
  }

  closeModal() {
    if (this.qaiDialogRef) {
      this.qaiDialogRef.close();
    }
  }

  fetchSimulationsData(): void {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    this.networkRequestInProgress = true;

    forkJoin({
      allPublished: this.authorService.findPublishedCallsByAccountUuid(
        accountUuid
      ),
      assigned: this.assignmentsService.getSimulationsByModuleAssignmentUuid(
        this.moduleAssignmentUuid
      ),
    }).subscribe(
      (result: any) => {
        const allPublished: IModuleWithCalls[] = result.allPublished;
        const assigned: ISimulationByModuleAssignment = result.assigned;
        this.allModulesWithCalls = allPublished;
        this.showAttemptsAllowed =
          assigned.moduleAssignmentType !==
          AssignmentTypeEnum.PERSONALIZED_PRACTICE;

        const assignedList =
          assigned && Array.isArray(assigned.simulations)
            ? assigned.simulations
            : [];
        const assignedIds = assignedList.map(
          (sim: any) => sim.callUuid || sim.id
        );
        this.treeData = this.transformToTreeData(
          allPublished,
          assignedIds
        );

        this.maxCallOrder = assignedList.length
          ? Math.max(...assignedList.map((sim: any) => sim.callOrder || 0))
          : 0;
        this.networkRequestInProgress = false;
      },
      () => {
        this.treeData = [];
        this.networkRequestInProgress = false;
      }
    );
  }

  private transformToTreeData(
    modules: IModuleWithCalls[],
    assignedIds: string[]
  ): IParsedModuleWithSimulations[] {
    return modules.map((module) => ({
      uuid: module.moduleUuid,
      name: module.moduleName,
      expanded: false,
      users: module.calls.map((call) => ({
        uuid: call.callUuid,
        name: call.callName,
        selected: false,
        disabled: assignedIds.includes(call.callUuid),
        teamUuid: module.moduleUuid,
        teamName: module.moduleName,
        email: '',
      })),
      children: [],
    }));
  }

  onRemove(id: string): void {
    this.selectedSimulations = this.selectedSimulations.filter(
      (sim) => sim.id !== id
    );
    this.selectedSimulationIds = [
      ...this.selectedSimulations.map((sim) => sim.id),
    ];
    this.updateFieldLabel();
  }

  onSelectionSave(data: SelectedOptions[]): void {
    const selectedSimulationsData = data as unknown as IParsedSimulation[];
    const selectedIds = selectedSimulationsData.map((sim) => sim.uuid);

    const existingIds = this.selectedSimulations.map((sim) => sim.id);
    const newIds = selectedIds.filter((id) => !existingIds.includes(id));

    const existingSimulations = this.selectedSimulations.filter((sim) =>
      selectedIds.includes(sim.id)
    );

    const newSimulations = newIds.map((id) => {
      let simulationName = '';
      for (const module of this.allModulesWithCalls) {
        const call = module.calls.find((c) => c.callUuid === id);
        if (call) {
          simulationName = call.callName;
          break;
        }
      }

      return {
        id: id,
        name: simulationName,
        attempts: attemptsOptions[0].value,
        attemptsOptions,
      };
    });

    this.selectedSimulations = [...existingSimulations, ...newSimulations];
    this.selectedSimulationIds = [...selectedIds];
    this.updateFieldLabel();
  }

  onRemoveAll(): void {
    this.selectedSimulations = [];
    this.selectedSimulationIds = [];
    if (this.treeSelectComponent) {
      this.treeSelectComponent.deselectAll();
    }
    this.updateFieldLabel();
  }

  onSimulationListSelectionChange(reorderedSimulations: DraggableItem[]): void {
    this.selectedSimulations = reorderedSimulations.map((item) => {
      const simulation = this.selectedSimulations.find(
        (sim) => sim.id === item['id']
      );
      return simulation!;
    });
    this.selectedSimulationIds = [
      ...this.selectedSimulations.map((sim) => sim.id),
    ];
  }

  onAttemptsChange(id: string, attempts: string): void {
    this.selectedSimulations = this.selectedSimulations.map((sim) => {
      if (sim.id === id) {
        return { ...sim, attempts };
      }
      return sim;
    });
  }

  private updateFieldLabel(): void {
    const baseLabel = 'Search for simulation';
    this.fieldLabel = this.selectedSimulations.length > 0
      ? `${baseLabel} (${this.selectedSimulations.length} selected)`
      : baseLabel;
  }

  assignSimulationsToAssignment(): void {
    if (!this.moduleAssignmentUuid || this.selectedSimulations.length === 0) {
      return;
    }
    this.networkRequestInProgress = true;
    const payload = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      simulations: this.selectedSimulations.map((sim, index) => ({
        callUuid: sim.id,
        numberOfAttemptsAllowed:
          sim.attempts === attemptsOptions[0].value
            ? null
            : Number(sim.attempts),
        callOrder: this.maxCallOrder + index + 1,
      })),
    };

    this.assignmentsService.addSimulationsToAssignment(payload).subscribe({
      next: () => {
        this.simulationAssigned.emit();
        this.networkRequestInProgress = false;
      },
      error: () => {
        this.networkRequestInProgress = false;
      },
    });
  }
}
